<template>
    <div class="gp">
        <!-- Barre de recherche + bouton Nouveau -->
        <div class="d-flex align-items-center gap-2 mb-2">
            <input v-model="search" type="text" class="form-control form-control-sm" placeholder="Rechercher..."
                @keyup.enter="onSearch" />
            <button class="btn btn-sm btn-outline-primary text-nowrap" @click="$emit('start-create')"
                :disabled="isDrawing">
                Nouveau
            </button>
        </div>

        <div class="list-group mb-3 small">
            <button v-for="g in geofencings" :key="g.id"
                class="list-group-item list-group-item-action d-flex align-items-center justify-content-between"
                :class="{ active: g.id === selectedId }" @click="$emit('select', g.id)">
                <span class="text-truncate">{{ g.name }}</span>
            </button>
            <div v-if="!geofencings?.length && !loading" class="text-muted px-2">Aucun geofencing</div>
        </div>

        <!-- Zone d’actions selon état -->
        <div v-if="creating" class="card card-body p-2 mb-2">
            <label class="form-label mb-1 small">Nom</label>
            <input v-model="draftName" type="text" class="form-control form-control-sm" placeholder="Ex: Zone dépôt" />
            <div class="small text-muted mt-2">Clique sur la carte pour ajouter des points (≥ 3), puis “Enregistrer”.
            </div>
            <div class="d-grid gap-2 mt-2">
                <button class="btn btn-sm btn-primary" :disabled="!canSaveCreate"
                    @click="$emit('save-create', (draftName || '').trim())">
                    Enregistrer
                </button>
                <small v-if="draftCount < 3" class="text-danger">Ajoute au moins 3 points sur la carte.</small>
                <button class="btn btn-sm btn-outline-secondary" @click="$emit('cancel-edit')">Annuler</button>
            </div>
        </div>

        <div v-else-if="selectedId">
            <!-- L’utilisateur doit explicitement demander les options de gestion -->
            <button type="button" class="btn btn-sm btn-link text-secondary text-decoration-none w-100 text-start mb-2"
                @click="showAdvanced = !showAdvanced">
                <span v-if="!showAdvanced">Modifier le geofencing…</span>
                <span v-else>Annuler</span>
            </button>

            <div v-if="showAdvanced" class="gp-advanced card card-body p-2">
                <div class="d-grid gap-2 mb-2">
                    <button class="btn btn-sm btn-outline-primary" :disabled="isDrawing" @click="$emit('start-edit')">
                        Éditer les points
                    </button>
                    <button class="btn btn-sm btn-outline-danger" :disabled="isDrawing"
                        @click="$emit('delete', selectedId)">
                        Supprimer
                    </button>
                </div>
                <div class="input-group input-group-sm">
                    <input v-model="rename" type="text" class="form-control" placeholder="Renommer..." />
                    <button class="btn btn-outline-secondary" :disabled="!renameClean" @click="saveRename">OK</button>
                </div>
            </div>
        </div>

        <div v-else class="text-muted small">Sélectionne un geofencing dans la liste, ou crée-en un.</div>

        <div v-if="isDrawing && !creating" class="card card-body p-2 mt-3">
            <div class="small text-muted">Clique sur la carte pour redéfinir tous les points (≥ 3), puis “Enregistrer”.
            </div>
            <div class="d-grid gap-2 mt-2">
                <button class="btn btn-sm btn-primary" :disabled="!canSaveEdit"
                    @click="$emit('save-edit')">Enregistrer</button>
                <button class="btn btn-sm btn-outline-secondary" @click="$emit('cancel-edit')">Annuler</button>
            </div>
        </div>

        <div v-if="loading" class="text-muted small mt-3">Chargement…</div>
    </div>
</template>

<script>
import GeofencingService from "@/services/geofencing.service";
export default {
    name: "RightGeofencingPanel",
    props: {
        clientId: { type: Number, required: false },
        geofencings: { type: Array, default: () => [] },
        selectedId: { type: Number, default: null },
        loading: { type: Boolean, default: false },
        isDrawing: { type: Boolean, default: false },
        draftCount: { type: Number, default: 0 },
    },
    emits: ["refresh", "select", "start-create", "start-edit", "cancel-edit", "save-create", "save-edit", "delete"],
    data() {
        return {
            search: "",
            rename: "",
            draftName: "",
            showAdvanced: false,
        };
    },
    computed: {
        creating() {
            return this.isDrawing && !this.selectedId;
        },
        renameClean() {
            return (this.rename || "").trim().length > 0;
        },
        canSaveCreate() {
            return (this.draftName || "").trim().length > 0 && this.draftCount >= 3;
        },
        canSaveEdit() {
            return this.draftCount >= 3;
        },
    },
    watch: {
        selectedId: {
            immediate: true,
            handler() {
                const sel = this.geofencings.find(g => g.id === this.selectedId);
                this.rename = sel ? sel.name : "";
                this.showAdvanced = false;
            },
        },
        // Recherche "live" avec délai comme SearchDisplay
        search(val) {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.$emit("refresh", (val || "").trim());
            }, 400);
        },
    },
    methods: {
        async onSearch() {
            // Enter force une recherche immédiate avec la valeur actuelle
            this.$emit("refresh", (this.search || "").trim());
        },
        async saveRename() {
            if (!this.selectedId || !this.renameClean) return;
            await GeofencingService.updateName(this.selectedId, (this.rename || "").trim());
            this.$emit("refresh");
        },
    },
};
</script>

<style scoped>
.gp {
    font-size: .95rem;
    padding: .25rem 0;
}

.list-group-item.active {
    background: #0d6efd;
    border-color: #0d6efd;
}

.gp-advanced {
    margin-top: .25rem;
}
</style>
